import Phaser from "phaser";

import { OFFLINE_REWARD_ART } from "../config/artAssets";
import { advanceAstrolabeBobTime, getAstrolabeBobOffset } from "./AstrolabeMotion";
import {
  ASTROLABE_3D_LOGICAL_SIZE,
  ASTROLABE_3D_CONFIG,
  Astrolabe3DRenderer,
  advanceRingPhase,
  type Astrolabe3DRenderState,
  type AstrolabeRingId,
} from "./Astrolabe3DRenderer";

export type OfflineRewardSceneCallbacks = {
  onCollectRequested(): void;
  onCollectComplete(): void;
  onLoadProgress?(progress: number): void;
  onLoadError?(): void;
  onReady?(): void;
  onRevolution?(ring: AstrolabeRingId): void;
};

const ASTROLABE_SCALE_AT_360 = 0.91;

export class OfflineRewardScene extends Phaser.Scene {
  readonly #callbacks: OfflineRewardSceneCallbacks;
  readonly #renderScale: number;
  #reducedMotion: boolean;
  #layoutWidth = 0;
  #layoutHeight = 0;
  #centerX = 0;
  #centerY = 0;
  #astrolabeScale = 1;
  #collecting = false;
  #particleCursor = 0;
  #motionActive = false;
  #loadFailed = false;
  #outerElapsedMs = 0;
  #innerElapsedMs = 0;
  #bobElapsedMs = 0;
  #motionState: Astrolabe3DRenderState = {
    outerPhase: ASTROLABE_3D_CONFIG.outer.startPhase,
    innerPhase: ASTROLABE_3D_CONFIG.inner.startPhase,
    ringScale: 1,
    ringAlpha: 1,
    coreScale: 1,
    coreY: 0,
    coreGlow: 0.12,
  };

  #background!: Phaser.GameObjects.Image;
  #shade!: Phaser.GameObjects.Rectangle;
  #shadow!: Phaser.GameObjects.Ellipse;
  #assembly!: Phaser.GameObjects.Container;
  #astrolabe!: Phaser.GameObjects.Image;
  #renderer!: Astrolabe3DRenderer;
  #hitArea!: Phaser.GameObjects.Zone;
  #particles: Phaser.GameObjects.Arc[] = [];
  #ambientTweens: Phaser.Tweens.Tween[] = [];
  #moteTimer: Phaser.Time.TimerEvent | null = null;

  constructor(callbacks: OfflineRewardSceneCallbacks, reducedMotion: boolean, renderScale: number) {
    super({ key: "OfflineReward" });
    this.#callbacks = callbacks;
    this.#reducedMotion = reducedMotion;
    this.#renderScale = renderScale;
  }

  preload(): void {
    this.load.on(Phaser.Loader.Events.PROGRESS, (progress: number) => this.#callbacks.onLoadProgress?.(progress));
    this.load.on(Phaser.Loader.Events.FILE_LOAD_ERROR, () => {
      if (this.#loadFailed) return;
      this.#loadFailed = true;
      this.#callbacks.onLoadError?.();
    });
    this.load.image(OFFLINE_REWARD_ART.background.key, OFFLINE_REWARD_ART.background.url);
    this.load.image(OFFLINE_REWARD_ART.astrolabe.core.key, OFFLINE_REWARD_ART.astrolabe.core.url);
    this.load.image(OFFLINE_REWARD_ART.astrolabe.outerRing.key, OFFLINE_REWARD_ART.astrolabe.outerRing.url);
    this.load.image(OFFLINE_REWARD_ART.astrolabe.innerRing.key, OFFLINE_REWARD_ART.astrolabe.innerRing.url);
  }

  create(): void {
    if (this.#loadFailed) return;
    this.#background = this.add.image(0, 0, OFFLINE_REWARD_ART.background.key).setDepth(0);
    this.#shade = this.add.rectangle(0, 0, 10, 10, 0x061c26, 0.28).setOrigin(0).setDepth(1);
    this.#shadow = this.add.ellipse(0, 0, 154, 24, 0x041318, 0.24).setDepth(2);

    const texture = this.textures.createCanvas(
      "offline-astrolabe-hd-orb-v7",
      Math.round(ASTROLABE_3D_LOGICAL_SIZE * this.#renderScale),
      Math.round(ASTROLABE_3D_LOGICAL_SIZE * this.#renderScale),
    );
    if (texture === null) throw new Error("Не удалось создать текстуру астролябии");
    this.#renderer = new Astrolabe3DRenderer(texture, {
      outer: {
        source: this.textures.get(OFFLINE_REWARD_ART.astrolabe.outerRing.key).getSourceImage() as CanvasImageSource,
        width: OFFLINE_REWARD_ART.astrolabe.outerRing.width,
        height: OFFLINE_REWARD_ART.astrolabe.outerRing.height,
        majorAngle: 0,
        majorDiameter: OFFLINE_REWARD_ART.astrolabe.outerRing.width,
        minorDiameter: OFFLINE_REWARD_ART.astrolabe.outerRing.height,
        centerlineRatio: 0.9,
      },
      inner: {
        source: this.textures.get(OFFLINE_REWARD_ART.astrolabe.innerRing.key).getSourceImage() as CanvasImageSource,
        width: OFFLINE_REWARD_ART.astrolabe.innerRing.width,
        height: OFFLINE_REWARD_ART.astrolabe.innerRing.height,
        majorAngle: 0,
        majorDiameter: OFFLINE_REWARD_ART.astrolabe.innerRing.width,
        minorDiameter: OFFLINE_REWARD_ART.astrolabe.innerRing.height,
        centerlineRatio: 0.888,
      },
      core: {
        source: this.textures.get(OFFLINE_REWARD_ART.astrolabe.core.key).getSourceImage() as CanvasImageSource,
        width: OFFLINE_REWARD_ART.astrolabe.core.width,
        height: OFFLINE_REWARD_ART.astrolabe.core.height,
        displayDiameter: ASTROLABE_3D_CONFIG.coreRadius * 2,
      },
    }, this.#renderScale);
    this.#renderer.render(this.#motionState);
    this.#astrolabe = this.add
      .image(0, 0, "offline-astrolabe-hd-orb-v7")
      .setDisplaySize(ASTROLABE_3D_LOGICAL_SIZE, ASTROLABE_3D_LOGICAL_SIZE);
    this.#assembly = this.add.container(0, 0, [this.#astrolabe]).setDepth(4);

    for (let index = 0; index < 14; index += 1) {
      const color = index % 3 === 0 ? 0xe8c783 : index % 2 === 0 ? 0xbff8f2 : 0x69d8d1;
      this.#particles.push(
        this.add.circle(0, 0, index % 4 === 0 ? 1.6 : 1.1, color, 0).setDepth(7).setVisible(false),
      );
    }

    this.#hitArea = this.add.zone(0, 0, 300, 300).setDepth(8).setInteractive({ useHandCursor: true });
    this.#hitArea.on(Phaser.Input.Events.POINTER_DOWN, () => {
      if (!this.#collecting) this.#callbacks.onCollectRequested();
    });

    this.scale.on(Phaser.Scale.Events.RESIZE, this.#layout, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.#stopAmbientMotion, this);
    this.#layout({ width: this.scale.width, height: this.scale.height } as Phaser.Structs.Size);
    this.#callbacks.onReady?.();
  }

  override update(_time: number, delta: number): void {
    if (!this.#motionActive || this.#collecting) return;
    const step = Phaser.Math.Clamp(delta, 0, 50);
    this.#motionState.outerPhase = advanceRingPhase(
      this.#motionState.outerPhase,
      step,
      ASTROLABE_3D_CONFIG.outer,
    );
    this.#motionState.innerPhase = advanceRingPhase(
      this.#motionState.innerPhase,
      step,
      ASTROLABE_3D_CONFIG.inner,
    );
    this.#outerElapsedMs += step;
    this.#innerElapsedMs += step;
    this.#bobElapsedMs = advanceAstrolabeBobTime(this.#bobElapsedMs, step);
    const bobOffset = getAstrolabeBobOffset(this.#bobElapsedMs);
    const lift = (-bobOffset + 3) / 6;
    this.#assembly.setY(this.#centerY + bobOffset);
    this.#shadow
      .setAlpha(0.22 - lift * 0.04)
      .setScale(this.#astrolabeScale * (1 - lift * 0.05), this.#astrolabeScale);
    this.#reportRevolutions("outer", ASTROLABE_3D_CONFIG.outer.revolutionMs);
    this.#reportRevolutions("inner", ASTROLABE_3D_CONFIG.inner.revolutionMs);
    this.#renderer.render(this.#motionState);
  }

  collect(): void {
    if (this.#collecting || !this.sys.isActive()) return;
    this.#collecting = true;
    if (this.#hitArea.input !== null) this.#hitArea.input.enabled = false;
    this.#stopAmbientMotion();

    if (this.#reducedMotion) {
      this.#motionState.coreScale = 0.99;
      this.#renderer.render(this.#motionState);
      this.time.delayedCall(160, () => this.#callbacks.onCollectComplete());
      return;
    }

    this.#ambientTweens.push(
      this.tweens.add({
        targets: this.#motionState,
        outerPhase: this.#motionState.outerPhase + Phaser.Math.DegToRad(34),
        innerPhase: this.#motionState.innerPhase - Phaser.Math.DegToRad(52),
        ringScale: 1.055,
        ringAlpha: 0.22,
        duration: 560,
        ease: "Cubic.easeInOut",
        onUpdate: () => this.#renderer.render(this.#motionState),
      }),
      this.tweens.add({
        targets: this.#motionState,
        coreScale: 1.055,
        coreY: -3,
        duration: 230,
        yoyo: true,
        ease: "Sine.easeInOut",
        onUpdate: () => this.#renderer.render(this.#motionState),
      }),
      this.tweens.add({
        targets: this.#motionState,
        coreGlow: 0.78,
        duration: 210,
        yoyo: true,
        ease: "Quad.easeOut",
        onUpdate: () => this.#renderer.render(this.#motionState),
      }),
      this.tweens.add({
        targets: this.#assembly,
        y: this.#centerY - 7,
        duration: 520,
        ease: "Cubic.easeOut",
      }),
      this.tweens.add({
        targets: this.#shadow,
        alpha: 0.08,
        scaleX: this.#astrolabeScale * 0.8,
        duration: 440,
        ease: "Cubic.easeOut",
      }),
    );
    this.time.delayedCall(170, () => this.#emitBurst());
    this.time.delayedCall(720, () => this.#callbacks.onCollectComplete());
  }

  setReducedMotion(reduced: boolean): void {
    if (this.#reducedMotion === reduced) return;
    this.#reducedMotion = reduced;
    if (this.sys.isActive() && !this.#collecting) this.#startAmbientMotion();
  }

  #reportRevolutions(ring: AstrolabeRingId, revolutionMs: number): void {
    const elapsed = ring === "outer" ? this.#outerElapsedMs : this.#innerElapsedMs;
    if (elapsed < revolutionMs) return;
    if (ring === "outer") this.#outerElapsedMs -= revolutionMs;
    else this.#innerElapsedMs -= revolutionMs;
    this.#callbacks.onRevolution?.(ring);
  }

  #layout(size: Phaser.Structs.Size): void {
    const physicalWidth = Math.round(size.width);
    const physicalHeight = Math.round(size.height);
    const width = Math.round(physicalWidth / this.#renderScale);
    const height = Math.round(physicalHeight / this.#renderScale);
    if (width <= 0 || height <= 0 || (width === this.#layoutWidth && height === this.#layoutHeight)) return;
    this.#layoutWidth = width;
    this.#layoutHeight = height;
    this.cameras.main
      .setViewport(0, 0, physicalWidth, physicalHeight)
      .setZoom(this.#renderScale)
      .centerOn(width / 2, height / 2);

    const backgroundScale = Math.max(
      width / OFFLINE_REWARD_ART.background.width,
      height / OFFLINE_REWARD_ART.background.height,
    );
    this.#background.setPosition(width / 2, height / 2).setScale(backgroundScale);
    this.#shade.setSize(width, height);

    this.#centerX = width / 2;
    this.#centerY = Phaser.Math.Clamp(height * 0.49, 282, 420);
    this.#astrolabeScale = ASTROLABE_SCALE_AT_360 * Phaser.Math.Clamp(width / 360, 0.82, 1.08);
    this.#assembly.setPosition(this.#centerX, this.#centerY).setScale(this.#astrolabeScale);
    this.#shadow
      .setPosition(this.#centerX, this.#centerY + 121 * this.#astrolabeScale)
      .setScale(this.#astrolabeScale);
    this.#hitArea
      .setPosition(this.#centerX, this.#centerY)
      .setSize(300 * this.#astrolabeScale, 300 * this.#astrolabeScale);

    if (!this.#collecting) this.#startAmbientMotion();
  }

  #startAmbientMotion(): void {
    this.#stopAmbientMotion();
    if (this.#collecting) return;

    this.#motionState.ringScale = 1;
    this.#motionState.ringAlpha = 1;
    this.#motionState.coreScale = 1;
    this.#motionState.coreY = 0;
    this.#motionState.coreGlow = this.#reducedMotion ? 0 : 0.08;
    this.#bobElapsedMs = 0;
    this.#assembly.setPosition(this.#centerX, this.#centerY).setScale(this.#astrolabeScale).setAlpha(1);
    this.#shadow.setAlpha(0.24).setScale(this.#astrolabeScale);
    this.#renderer.render(this.#motionState);

    if (this.#reducedMotion) return;
    this.#motionActive = true;
    this.#moteTimer = this.time.addEvent({ delay: 820, loop: true, callback: () => this.#emitMote() });
  }

  #stopAmbientMotion(): void {
    this.#motionActive = false;
    this.#ambientTweens.forEach((tween) => tween.stop());
    this.#ambientTweens = [];
    this.#moteTimer?.destroy();
    this.#moteTimer = null;
    this.tweens.killTweensOf(this.#assembly);
    this.tweens.killTweensOf(this.#shadow);
    this.tweens.killTweensOf(this.#motionState);
  }

  #emitMote(): void {
    const particle = this.#particles[this.#particleCursor];
    if (particle === undefined) return;
    const index = this.#particleCursor;
    this.#particleCursor = (this.#particleCursor + 1) % this.#particles.length;
    const phase = index * 2.399;
    const startX = this.#centerX + Math.cos(phase) * (34 + (index % 4) * 11) * this.#astrolabeScale;
    const startY = this.#centerY + (48 + (index % 5) * 13) * this.#astrolabeScale;
    this.tweens.killTweensOf(particle);
    particle.setPosition(startX, startY).setAlpha(0.42).setScale(0.7).setVisible(true);
    this.tweens.add({
      targets: particle,
      x: startX + Math.sin(phase) * 12,
      y: startY - (31 + (index % 3) * 9),
      alpha: 0,
      scale: 0.2,
      duration: 1_150,
      ease: "Sine.easeOut",
      onComplete: () => particle.setVisible(false),
    });
  }

  #emitBurst(): void {
    const count = Math.min(10, this.#particles.length);
    for (let index = 0; index < count; index += 1) {
      const particle = this.#particles[(this.#particleCursor + index) % this.#particles.length];
      if (particle === undefined) continue;
      const angle = -Math.PI * 0.92 + (Math.PI * 0.84 * index) / Math.max(1, count - 1);
      const distance = 46 + (index % 3) * 13;
      this.tweens.killTweensOf(particle);
      particle.setPosition(this.#centerX, this.#centerY - 5).setAlpha(0.78).setScale(1).setVisible(true);
      this.tweens.add({
        targets: particle,
        x: this.#centerX + Math.cos(angle) * distance,
        y: this.#centerY + Math.sin(angle) * distance,
        alpha: 0,
        scale: 0.25,
        duration: 460,
        ease: "Cubic.easeOut",
        onComplete: () => particle.setVisible(false),
      });
    }
    this.#particleCursor = (this.#particleCursor + count) % this.#particles.length;
  }
}
